import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Lock, CreditCard, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { bapi, fmtDate, fmtMoney, startHostedPayment } from '@/lib/billingApi';
import { trackInitiateCheckout } from '@/lib/metaPixel';
import { CardBrands } from './CardBrands';
import { TestModeBanner } from './StatusPill';
import { CouponField } from './CouponField';

let catalogPromise = null;
export const loadCatalog = () => {
  if (!catalogPromise) catalogPromise = bapi('/billing/catalog').catch((e) => { catalogPromise = null; throw e; });
  return catalogPromise;
};

const addInterval = (interval) => {
  const d = new Date();
  if (interval === 'year') d.setFullYear(d.getFullYear() + 1); else d.setMonth(d.getMonth() + 1);
  return d;
};

const Row = ({ label, value, strong, testId }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <span className={strong ? 'text-sm font-bold text-slate-900' : 'text-sm text-slate-500'}>{label}</span>
    <span className={`whitespace-nowrap ${strong ? 'text-xl font-black text-[#00247D]' : 'text-sm font-semibold text-slate-800 text-right'}`} data-testid={testId}>{value}</span>
  </div>
);

const MethodOption = ({ active, disabled, onClick, testId, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId} aria-pressed={active}
    className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-[border-color,background-color] duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
      active ? 'border-[#00247D] bg-[#00247D]/5' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
    {children}
  </button>
);

const per = (i) => (i.interval === 'year' ? 'year' : 'month');

const FlexTerms = ({ product, autoRenew }) => (
  <p className="text-xs text-slate-500 leading-relaxed" data-testid="checkout-renewal-terms">
    {autoRenew ? 'Recurring: ' : 'One-time payment: '}
    {product.includes.map((i, n) => (
      <span key={i.id}>{n ? '; ' : ''}{i.short} {autoRenew ? 'renews on' : 'runs until'} <b className="text-slate-700">{fmtDate(addInterval(i.interval))}</b>
        {autoRenew && <> for <b className="text-slate-700">{fmtMoney(i.price, product.currency)}</b> every {per(i)}</>}</span>
    ))}
    {autoRenew ? '. Each renews on its own schedule until you cancel it from Billing.' : '. Nothing renews automatically.'}
  </p>
);

const consentText = (product, renewal) => (product.interval === 'flex'
  ? `I authorise Schedule Guru to charge this card ${product.includes.map((i) => `${fmtMoney(i.price, product.currency)} each ${per(i)} for ${i.short}`).join(' and ')} on renewal until I cancel.`
  : `I authorise Schedule Guru to charge this card ${fmtMoney(renewal, product.currency)} each ${product.interval} on renewal until I cancel.`);

const RenewalTerms = ({ product, amount, autoRenew, end }) => (product.interval === 'flex' ? <FlexTerms product={product} autoRenew={autoRenew} /> : (
  <p className="text-xs text-slate-500 leading-relaxed" data-testid="checkout-renewal-terms">
    {autoRenew
      ? <>Recurring: renews automatically on <b className="text-slate-700">{fmtDate(end)}</b> for <b className="text-slate-700">{fmtMoney(amount, product.currency)}</b> every {product.interval}, until you cancel. Cancel anytime from Billing and keep access until the period ends.</>
      : <>One-time payment: access runs until <b className="text-slate-700">{fmtDate(end)}</b> and will not renew.</>}
  </p>
));

export const CheckoutDialog = ({ open, productId, onClose, onPayPal }) => {
  const [catalog, setCatalog] = useState(null);
  const [method, setMethod] = useState('card');
  const [autoRenew, setAutoRenew] = useState(true);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState(null);

  useEffect(() => { if (open) loadCatalog().then(setCatalog).catch(() => toast.error('Could not load checkout')); }, [open]);
  useEffect(() => { if (open) { setConsent(false); setAutoRenew(true); setBusy(false); setCoupon(null); } }, [open, productId]);
  const product = useMemo(() => catalog?.products.find((p) => p.id === productId), [catalog, productId]);
  const cardOnly = !!product?.card_only;
  useEffect(() => { if (catalog) setMethod(catalog.card_enabled || cardOnly ? 'card' : 'paypal'); }, [catalog, cardOnly]);

  const flex = product?.interval === 'flex';
  const end = product && !flex ? addInterval(product.interval) : null;
  const card = method === 'card';
  const canPay = product && !busy && (!card || catalog.card_enabled) && (!card || !autoRenew || consent);
  const applied = card ? coupon : null;
  const today = applied ? applied.total : product?.total;
  const renewal = applied ? applied.renewal_amount : product?.total;
  const changeCoupon = (c) => { setCoupon(c); setConsent(false); };

  const pay = async () => {
    trackInitiateCheckout({ value: today, currency: product.currency, planId: product.id });
    if (!card) { onClose(); onPayPal?.(product.id); return; }
    setBusy(true);
    try {
      await startHostedPayment('/billing/checkout', { product_id: product.id, auto_renew: autoRenew, consent, coupon_code: applied?.code });
    } catch (e) {
      toast.error(e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogHeader>
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-amber-600">Secure checkout</p>
          <DialogTitle className="text-2xl font-black uppercase text-[#00247D] leading-tight" style={{ fontFamily: 'Barlow Condensed' }} data-testid="checkout-product-name">
            {product?.name || 'Loading…'}
          </DialogTitle>
          <DialogDescription className="sr-only">Review your order and choose how to pay.</DialogDescription>
        </DialogHeader>
        {!product ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#00247D]" /></div> : (
          <div className="space-y-4">
            {catalog.test_mode && card && <TestModeBanner />}
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-2 divide-y divide-slate-200" data-testid="checkout-summary">
              <div>
                <Row label="Billing frequency" value={product.billing_frequency} testId="checkout-frequency" />
                {product.includes?.map((i) => <Row key={i.id} label={`Includes ${i.short}`} value={`${fmtMoney(i.price, product.currency)}${flex ? `/${per(i) === 'year' ? 'yr' : 'mo'}` : ''}`} testId={`checkout-includes-${i.id}`} />)}
                <Row label="Price" value={fmtMoney(product.price, product.currency)} testId="checkout-price" />
                <Row label="Tax" value={fmtMoney(product.tax, product.currency)} testId="checkout-tax" />
                <Row label="Currency" value={product.currency} />
              </div>
              {applied && <Row label={`Coupon ${applied.code} (${applied.percent_off}% off)`} value={`−${fmtMoney(applied.discount, product.currency)}`} testId="checkout-discount" />}
              <Row label="Total due today" value={fmtMoney(today, product.currency)} strong testId="checkout-total" />
            </div>
            {card && !product.no_coupons && <CouponField productId={product.id} applied={coupon} onChange={changeCoupon} />}

            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Payment method</p>
              <MethodOption active={card} disabled={!catalog.card_enabled} onClick={() => setMethod('card')} testId="checkout-method-card">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 font-bold text-slate-900"><CreditCard className="w-4 h-4 text-[#00247D]" />Credit or Debit Card</span>
                  <CardBrands />
                </div>
                <p className="text-xs text-slate-500 mt-1" data-testid="checkout-card-note">{catalog.card_enabled ? 'Visa and Mastercard, credit and debit, on the secure RBC Plug\'n Pay page.' : `Card payments are temporarily unavailable. ${cardOnly ? 'Please try again later.' : 'Please use PayPal.'}`}</p>
              </MethodOption>
              {cardOnly ? <p className="text-xs text-slate-500 pt-2" data-testid="checkout-card-only-note">This plan is paid by card only.</p> : (
                <MethodOption active={!card} onClick={() => setMethod('paypal')} testId="checkout-method-paypal">
                  <span className="font-bold text-slate-900">PayPal</span>
                  <p className="text-xs text-slate-500 mt-1">Pay with your PayPal account. Renews automatically through PayPal.</p>
                </MethodOption>
              )}
            </div>

            {card && (
              <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3 cursor-pointer" htmlFor="checkout-auto-renew">
                <span>
                  <span className="block text-sm font-bold text-slate-900">Renew automatically</span>
                  <span className="block text-xs text-slate-500">{flex ? 'Pro each month and Officials each year, same card' : `Charged to the same card each ${product.interval}`}</span>
                </span>
                <Switch id="checkout-auto-renew" checked={autoRenew} onCheckedChange={setAutoRenew} data-testid="checkout-auto-renew-switch" />
              </label>
            )}
            <RenewalTerms product={product} amount={renewal} autoRenew={!card || autoRenew} end={end} />
            {card && autoRenew && (
              <label className="flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer" htmlFor="checkout-consent">
                <Checkbox id="checkout-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" data-testid="checkout-consent-checkbox" />
                <span>{consentText(product, renewal)}</span>
              </label>
            )}
            <div className="sticky -bottom-6 -mx-6 px-6 pt-3 pb-6 bg-white border-t border-slate-100 space-y-2 sm:static sm:m-0 sm:p-0 sm:border-0" data-testid="checkout-pay-bar">
              <Button onClick={pay} disabled={!canPay} className="w-full h-12 bg-[#00247D] hover:bg-[#001A5C] text-white font-bold text-base transition-transform active:scale-[0.99]" data-testid="checkout-pay-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : card ? <Lock className="w-4 h-4 mr-2" /> : null}
                {card ? `Pay ${fmtMoney(today, product.currency)} by card` : 'Continue with PayPal'}
              </Button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400"><ShieldCheck className="w-3.5 h-3.5 shrink-0" />Card details are entered on the bank's secure page and never stored by Schedule Guru.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
